import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Wand2 } from "lucide-react";
import { generateItinerary } from "../lib/api";
import { toast } from "sonner";
import { MicButton } from "./MicButton";

const SUGGESTIONS = [
  "Luxury Bali sunset trip",
  "Hidden Tokyo cyberpunk reel journey",
  "Old money Italy café route",
  "Barcelona colourful street content walk",
];

export const PromptBar = ({ defaultValue = "", autoFocus = false }) => {
  const [value, setValue] = useState(defaultValue);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (override) => {
    const prompt = (override ?? value).trim();
    if (prompt.length < 4) {
      toast.error("Tell us your vibe — at least a few words");
      return;
    }
    setLoading(true);
    try {
      const itin = await generateItinerary(prompt);
      navigate(`/itinerary/${itin.id}`, { state: { itin } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <form
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className="glass-strong rounded-2xl sm:rounded-full p-2 sm:pl-6 sm:pr-2 sm:py-2 flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch sm:items-center ring-neon"
        data-testid="prompt-form"
      >
        <Wand2 className="hidden sm:block h-5 w-5 text-zinc-400 shrink-0" />
        <input
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Plan me a luxury hidden-gem Italy trip with sunset reel spots…"
          data-testid="prompt-input"
          className="flex-1 bg-transparent outline-none placeholder:text-zinc-500 text-white text-base sm:text-lg font-body py-3 sm:py-2.5 px-4 sm:px-0"
          disabled={loading}
        />
        <div className="flex items-center gap-2 px-2 sm:px-0">
          <MicButton
            size="sm"
            testId="prompt-mic-btn"
            onTranscript={(text) => setValue((v) => (v ? `${v.trim()} ${text}` : text))}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="generate-itinerary-btn"
            className="flex-1 sm:flex-initial gradient-pink-cyan text-black font-display font-bold uppercase tracking-[0.18em] text-xs sm:text-sm rounded-xl sm:rounded-full px-5 py-3 sm:py-3 inline-flex items-center justify-center gap-2 hover:opacity-95 active:scale-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            <span>{loading ? "Crafting" : "Generate My Vibe Trip"}</span>
          </button>
        </div>
      </form>
      <div className="mt-4 flex flex-wrap gap-2 justify-start items-center">
        {SUGGESTIONS.map((s, i) => (
          <button
            key={i}
            data-testid={`suggestion-chip-${i}`}
            onClick={() => { setValue(s); submit(s); }}
            disabled={loading}
            className="text-[11px] sm:text-xs font-medium text-zinc-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 transition"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
};
